import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import SiteApi from '../../Api/SiteApi/SiteApi';
import { formatCurrency } from '../../utils/formatters';

import { isThankYouLotto } from '../../utils/template';
import InfoBox from '../common/InfoBox';
import TextInput from '../common/TextInput';
import Loader from '../loader/Loader';

type RememberedCode = {
    value: string | null;
    discount: number | null;
    bonus: string | null;
    lottery: string | null;
    lotterySlug: string | null;
};

// TODO: add possibility to use state values instead of DOM elements
function PromoCode({ useplace, setDiscountValue = null, ticketPrice = 0 }) {
    const url = 'promo-code';
    const [rememberedCode, setRememberedCode] = useState<RememberedCode>({
        value: null,
        discount: null,
        bonus: null,
        lottery: null,
        lotterySlug: null,
    });
    const [isActive, setIsActive] = useState(false);
    const [inputCode, setInputCode] = useState('');
    const [errors, setErrors] = useState(null);
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const [pickerLotterySlug, setPickerLotterySlug] = useState(null);
    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        SiteApi.get(url, {
            params: { useplace },
            signal: controller.signal,
        })
            .then((response) => {
                if (isMounted) {
                    setIsActive(response.data.isActive);
                    setRememberedCode({
                        ...response.data.rememberedCode,
                        discount: parseFloat(response.data.rememberedCode.discount),
                    });
                    const inputElement = document.querySelector(
                        'input[type="hidden"][name="lottery"]'
                    ) as HTMLInputElement;
                    setPickerLotterySlug(inputElement?.value);
                }
            })
            .catch((error) => {
                // Only log if it's not an abort error
                if (error.name !== 'AbortError' && isMounted) {
                    console.error('Failed to fetch promo code data:', error);
                }
            });

        return () => {
            isMounted = false;
            // It's important because higher order components may reload, so we should cancel async calls
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (!rememberedCode.value) {
            return;
        }

        if (!['percentage_discount', 'amount_discount'].includes(rememberedCode.bonus)) {
            return;
        }

        if (!(!rememberedCode.lottery || rememberedCode.lotterySlug === pickerLotterySlug)) {
            return;
        }

        setDiscount();
    }, [rememberedCode, ticketPrice]);

    function applyCode() {
        setErrors(null);
        setMessage(null);
        setLoading(true);

        // Create a new AbortController for this specific request
        const controller = new AbortController();

        // Store the controller in the ref so we can access it later if needed
        abortControllerRef.current = controller;

        SiteApi
            .post(`${url}/apply`, { code: inputCode, useplace }, { signal: controller.signal })
            .then((response) => {
                setRememberedCode(response.data.code);
                setMessage(t('promoCodes.promoCodeHasBeenApplied'));
            })
            .catch((error) => {
                // Don't update state if this is an abort error
                if (error.name === 'AbortError') {
                    return;
                }

                if (error?.response?.status === 403) {
                    setIsActive(false);
                }
                setErrors(error.response.data.errors);
            })
            .finally(() => {
                // Only update loading state if not aborted
                if (abortControllerRef.current) {
                    setLoading(false);
                }
            });

        // Return the abort controller so it can be used to cancel the request if needed
        return controller;
    }

    function dispatchDiscountChangedEvent() {
        document.getElementById('paymentSubmit').dispatchEvent(new Event('discountChanged'));
    }

    function setDiscount() {
        const purchaseData = document.getElementById('purchaseSum').dataset;
        const discountAmount = rememberedCode.discount;

        purchaseData.discount_amount = String(discountAmount);
        purchaseData.discount_type = rememberedCode.bonus;

        if (typeof setDiscountValue === 'function') {
            let sumDiscounted = 0;
            const sumValue = parseFloat(purchaseData.sum) || 0;
            const discountAmountValue = discountAmount || 0;
            if (purchaseData.discount_type === 'amount_discount') {
                sumDiscounted = sumValue - discountAmountValue;
            } else if (purchaseData.discount_type === 'percentage_discount') {
                sumDiscounted = sumValue - sumValue * (discountAmountValue / 100);
            }

            setDiscountValue(sumDiscounted);
        }

        dispatchDiscountChangedEvent();
    }

    function clearDiscount() {
        const purchaseData = document.getElementById('purchaseSum').dataset;
        purchaseData.discount_amount = '';
        purchaseData.discount_type = '';

        dispatchDiscountChangedEvent();

        if (typeof setDiscountValue === 'function') {
            setDiscountValue(null);
        }
    }

    function getInputGroup() {
        return (
            <>
                {getHeader()}
                <div key="group" className="input-group">
                    {getInput()}
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!(inputCode.length > 0)}
                        onClick={() => applyCode()}
                    >
                        {t('promoCodes.apply')}
                    </button>
                    <span className="d-none" id="promo-code-not-applied-question">
                        {t('promoCodes.notAppliedQuestion')}
                    </span>
                </div>
            </>
        );
    }

    function clearCode() {
        const controller = new AbortController();

        SiteApi.post(`${url}/clear`, { useplace }, { signal: controller.signal })
            .then(() => {
                setRememberedCode({} as RememberedCode);
                setMessage(t('promoCodes.promoCodeHasBeenCleared'));

                if (useplace === 'purchase') {
                    clearDiscount();
                }
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    setErrors(error.response.data.errors);
                }
            });

        return controller;
    }

    function getRememberedCode() {
        return (
            <>
                {getHeader()}
                <p key="promo">
                    <i className="fa fa-info-circle" />{' '}
                    <Trans i18nKey="promoCodes.youUsedCode" code={rememberedCode.value}>
                        You used <b>{{ code: rememberedCode.value }}</b> promo code.
                    </Trans>{' '}
                    {getPromoDetails()}{' '}
                    <a onClick={() => clearCode()} className="promo-code-remove">
                        {t('Remove')}
                    </a>
                </p>
            </>
        );
    }

    function getPromoDetails() {
        let detailsMessage: string | React.JSX.Element = '';

        if (rememberedCode.bonus === 'ticket') {
            const { lottery } = rememberedCode;
            if (lottery) {
                detailsMessage = (
                    <Trans i18nKey="promoCodes.youWillReceiveTicket" lottery={lottery}>
                        You will receive free <b>{{ lottery }}</b> ticket after purchase.
                    </Trans>
                );
            }
        } else {
            const discount = formatDiscount();
            detailsMessage = (
                <Trans i18nKey="promoCodes.youWillReceiveDiscount" discount={discount}>
                    You will receive <b>{{ discount }}</b> discount.
                </Trans>
            );
            const { lottery } = rememberedCode;
            if (lottery) {
                detailsMessage = (
                    <>
                        {detailsMessage}{' '}
                        <Trans i18nKey="promoCodes.promoCodeRestrictedToLottery" lottery={lottery}>
                            This promo code applies exclusively on the <b>{{ lottery }}</b> lottery.
                        </Trans>
                    </>
                );
            }
        }

        return detailsMessage;
    }

    function formatDiscount() {
        let discountText: string;
        if (rememberedCode.bonus === 'amount_discount') {
            discountText = formatCurrency(rememberedCode.discount);
        } else if (rememberedCode.bonus === 'percentage_discount') {
            discountText = `${rememberedCode.discount}%`;
        }

        return discountText;
    }

    function getHeader() {
        return (
            <>
                {!isTemplateThankYouLotto && (
                    <h2 key="header" className="title text-center">
                        {t('promoCodes.promoCode')}
                    </h2>
                )}
                <InfoBox errors={errors} message={message} key="info-box" />
            </>
        );
    }

    function getInput() {
        return (
            <TextInput
                key="promo_code"
                name="promo_code"
                placeholder={t('promoCodes.promoCode')}
                value={inputCode}
                onChange={(event) => setInputCode(event.target.value)}
            />
        );
    }

    function getContent() {
        if (rememberedCode.value) {
            return getRememberedCode();
        }

        return isActive && getInputGroup();
    }

    return <Loader isLoading={loading} elementToShow={getContent()} />;
}

export default PromoCode;
